<template>
  <header :class="['main-header', { scrolled: isScrolled }]">
    <IAlert
      v-if="showBanner"
      size="md"
      class="transition-all duration-300 gradient-background"
      @dismiss="handlePromoDismiss"
      dismissible>
      <div class="flex items-center justify-center">
        <span class="font-medium text-white promo-text">
          {{ config.landing.show_offer_text }}
          <span v-if="config.landing.show_offer_link">
            <Link
              class="offer-link -link text-white font-medium"
              :href="config.landing.show_offer_link"
              :aria-label="config.landing.show_offer_link_text">
              {{ config.landing.show_offer_link_text }}
            </Link>
          </span>
          <span v-if="showLimit" class="countdown-timer">
            {{ getHumanTimeRemaining() }}
          </span>
        </span>
      </div>
    </IAlert>
    <IContainer>
      <nav>
        <div class="_display:flex _justify-content:space-between nav-bar">
          <div>
            <div class="_display:flex logo" style="height: 46px">
              <Link
                :href="safeRoute('home.index', '/')"
                aria-label="Climate64 Home"
                class="relative block h-[46px] w-[221px]">
                <!-- Dark logo -->
                <img
                  src="/images/logo-dark.svg"
                  width="221"
                  height="30"
                  alt="Climate64"
                  class="absolute inset-0 transition-opacity duration-300"
                  :class="{ 'opacity-0': !isScrolled, 'opacity-100': isScrolled }" />
                <!-- Light logo -->
                <img
                  src="/images/logo-light.svg"
                  width="221"
                  height="30"
                  alt="Climate64"
                  class="absolute inset-0 transition-opacity duration-300"
                  :class="{ 'opacity-0': isScrolled, 'opacity-100': !isScrolled }" />
              </Link>
            </div>
          </div>
          <div class="desktop-nav">
            <div class="_margin-right:1" role="navigation">
              <Link
                id="add-view-courses-action"
                class="nav-link -link transition-colors duration-300"
                :class="{ '-light': !isScrolled, '-dark': isScrolled }"
                :href="safeRoute('page.courses', '/page/courses')"
                aria-label="Courses">
                Courses
              </Link>
              <Link
                id="add-view-all-access-action"
                class="nav-link -link transition-colors duration-300"
                :class="{ '-light': !isScrolled, '-dark': isScrolled }"
                :href="safeRoute('page.pricing', '/pricing')"
                aria-label="All-Access">
                All-Access
              </Link>
              <Link
                id="add-view-teams-action"
                class="nav-link -link transition-colors duration-300"
                :class="{ '-light': !isScrolled, '-dark': isScrolled }"
                :href="safeRoute('teams.index', '/teams')"
                aria-label="For Teams">
                For Teams
              </Link>
              <Link
                id="add-view-blog-action"
                class="nav-link -link transition-colors duration-300"
                :class="{ '-light': !isScrolled, '-dark': isScrolled }"
                :href="safeRoute('blog.index', '/blog')"
                aria-label="Go to Blog link">
                Blog
              </Link>
            </div>
          </div>
          <div v-if="showWaitlist" class="desktop-nav">
            <Link
              v-if="currentUrl == '/'"
              id="add-new-join-waitlist-action"
              class="button -primary _text:semibold"
              href="#"
              v-scroll-to="'#bottom-cta-form'"
              aria-label="Join the waitlist button">
              Join the Waitlist
            </Link>
            <Link
              v-else
              id="add-new-join-waitlist-action"
              class="button -primary _text:semibold"
              :href="safeRoute('home.index', '/')"
              v-scroll-to="'#bottom-cta-form'"
              aria-label="Join the waitlist button">
              Join the Waitlist
            </Link>
          </div>
          <div class="_display:flex desktop-nav nav-actions">
            <Link
              id="add-view-login-action"
              class="nav-link _margin-right:1/2 -link transition-colors duration-300"
              :class="{ '-light': !isScrolled, '-dark': isScrolled }"
              :href="safeRoute('login', '/login')"
              aria-label="Go to Log In link">
              Log In
            </Link>
            <Link
              v-if="$page.props.auth && $page.props.auth.user"
              id="logout-action"
              class="button -primary _text:semibold"
              :href="safeRoute('logout', '/logout')"
              method="post"
              as="button"
              aria-label="Log Out">
              Log Out
            </Link>
            <Link
              v-else
              id="add-new-get-started-action"
              class="button -primary _text:semibold"
              :href="safeRoute('register', '/register')"
              aria-label="Get Started">
              Get Started
            </Link>
          </div>
          <button
            class="hamburger-menu transition-all duration-300"
            :class="{ 'is-active': isNavOpen }"
            @click="toggleNav"
            :aria-expanded="isNavOpen"
            aria-controls="mobile-nav"
            aria-label="Toggle navigation menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>
    </IContainer>

    <!-- Mobile Navigation -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform -translate-y-4"
      enter-to-class="opacity-100 transform translate-y-0"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 transform translate-y-0"
      leave-to-class="opacity-0 transform -translate-y-4">
      <div v-if="isNavOpen" id="mobile-nav" class="mobile-nav">
        <div class="mobile-nav-links">
          <Link
            id="add-view-courses-action"
            class="mobile-nav-link"
            :href="safeRoute('page.courses', '/page/courses')"
            @click="closeNavAfterClick"
            aria-label="Courses">
            Courses
          </Link>
          <Link
            id="add-view-all-access-mobile-action"
            class="mobile-nav-link"
            :href="safeRoute('page.pricing', '/pricing')"
            @click="closeNavAfterClick"
            aria-label="All-Access">
            All-Access
          </Link>
          <Link
            id="add-view-teams-action"
            class="mobile-nav-link"
            :href="safeRoute('teams.index', '/teams')"
            @click="closeNavAfterClick"
            aria-label="For Teams">
            For Teams
          </Link>
          <!-- <Link
            id="add-view-pricing-action"
            class="mobile-nav-link"
            :href="safeRoute('page.pricing', '/page/pricing')"
            @click="closeNavAfterClick"
            aria-label="Pricing">
            Pricing
          </Link> -->
          <Link
            id="add-view-blog-action"
            class="mobile-nav-link"
            :href="safeRoute('blog.index', '/blog')"
            @click="closeNavAfterClick"
            aria-label="Go to Blog link">
            Blog
          </Link>
          <Link
            id="add-view-roadmap-action"
            class="mobile-nav-link"
            :href="safeRoute('roadmap.index', '/roadmap')"
            @click="closeNavAfterClick"
            aria-label="Go to Roadmap link">
            Roadmap
          </Link>
          <Link
            id="add-view-login-action"
            class="mobile-nav-link"
            :href="safeRoute('login', '/login')"
            @click="closeNavAfterClick"
            aria-label="Go to Login link">
            Login
          </Link>
        </div>
        <div v-if="showWaitlist" class="mobile-nav-actions">
          <Link
            id="add-new-join-waitlist-action"
            class="button -primary -block _text:semibold"
            :href="currentUrl == '/' ? '#' : safeRoute('home.index', '/')"
            v-scroll-to="currentUrl == '/' ? '#bottom-cta-form' : null"
            @click="closeNavAfterClick"
            aria-label="Join the waitlist button">
            Join the Waitlist
          </Link>
        </div>
        <div v-else class="mobile-nav-actions">
          <Link
            v-if="$page.props.auth && $page.props.auth.user"
            id="logout-action"
            class="button -primary -block _text:semibold"
            :href="safeRoute('logout', '/logout')"
            method="post"
            as="button"
            @click="closeNavAfterClick"
            aria-label="Log Out">
            Log Out
          </Link>
          <Link
            v-else
            id="add-new-get-started-action"
            class="button -primary -block _text:semibold"
            :href="safeRoute('register', '/register')"
            @click="closeNavAfterClick"
            aria-label="Get Started">
            Get Started
          </Link>
        </div>
      </div>
    </Transition>
  </header>

  <Transition
    enter-active-class="transition-opacity duration-500"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-500"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0">
    <button
      v-show="showGoToTop"
      @click="scrollToTop"
      class="go-to-top-btn"
      aria-label="Go to top of page">
      <ChevronUpIcon class="h-6 w-6" />
    </button>
  </Transition>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
  import { Link, usePage } from '@inertiajs/vue3'
  import { ChevronUpIcon } from '@heroicons/vue/24/solid'
  import config from '@config'

  // Safe route helper function
  const safeRoute = (routeName, fallbackUrl) => {
    return typeof route !== 'undefined' ? route(routeName) : fallbackUrl
  }

  // Create a reactive reference to the config with a fallback
  const configData = ref(
    config || { landing: { show_banner: false, show_waitlist: false, show_offer_limit: false } },
  )

  // Create refs for conditional flags that depend on environment
  const isSSR = ref(typeof window === 'undefined')
  const isBrowser = ref(!isSSR.value)

  // Countdown configuration - easily change promo duration
  const promoDurationHours = ref(72) // Change this: 24, 48, 72, 168 (1 week), etc.

  // Countdown timer refs
  const timeRemaining = ref({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })
  const countdownInterval = ref(null)

  // Calculate promo end date based on duration
  const getPromoEndDate = () => {
    const now = new Date()
    return new Date(now.getTime() + promoDurationHours.value * 60 * 60 * 1000)
  }

  // Computed properties for conditional rendering
  const showBanner = computed(() => {
    return isBrowser.value && configData.value?.landing?.show_banner
  })

  const showLimit = computed(() => {
    return isBrowser.value && configData.value?.landing?.show_offer_limit
  })

  const showWaitlist = computed(() => {
    return configData.value?.landing?.show_waitlist
  })

  // nav refs
  const isScrolled = ref(false)
  const isNavOpen = ref(false)
  const showGoToTop = ref(false)
  const initialRender = ref(false)
  const currentUrl = usePage().url

  // Helper function to format time with leading zeros
  const formatTime = (time) => {
    return time.toString().padStart(2, '0')
  }

  // Convert time remaining to human-readable format
  const getHumanTimeRemaining = () => {
    const { days, hours, minutes } = timeRemaining.value

    if (days > 0) {
      return days === 1 ? '1 day left!' : `${days} days left`
    } else if (hours > 0) {
      return hours === 1 ? '1 hour left!' : `${hours} hours left!`
    } else if (minutes > 0) {
      return minutes === 1 ? '1 minute left!' : `${minutes} minutes left!`
    } else {
      return 'Ending soon!'
    }
  }

  // Calculate time remaining until promo ends
  const updateCountdown = () => {
    const now = new Date().getTime()
    const endTime = getPromoEndDate().getTime()
    const timeDiff = endTime - now

    if (timeDiff <= 0) {
      timeRemaining.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
      return
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)

    timeRemaining.value = { days, hours, minutes, seconds }
  }

  const handlePromoDismiss = () => {
    if (configData.value?.landing) {
      configData.value.landing.show_banner = false
    }
  }

  const toggleNav = () => {
    isNavOpen.value = !isNavOpen.value
    if (typeof document !== 'undefined') {
      document.body.style.overflow = isNavOpen.value ? 'hidden' : ''
    }
  }

  const closeNavAfterClick = () => {
    isNavOpen.value = false
    if (typeof document !== 'undefined') {
      document.body.style.overflow = ''
    }
  }

  const handleScroll = () => {
    if (typeof window !== 'undefined') {
      isScrolled.value = window.scrollY > 50
      showGoToTop.value = window.scrollY > 300
    }
  }

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const handleResize = () => {
    if (typeof window !== 'undefined' && window.innerWidth > 768) {
      isNavOpen.value = false
      document.body.style.overflow = ''
    }
  }

  const handleClickOutside = (event) => {
    const mobileNav = document.getElementById('mobile-nav')
    const hamburgerMenu = document.querySelector('.hamburger-menu')

    if (
      isNavOpen.value &&
      mobileNav &&
      !mobileNav.contains(event.target) &&
      hamburgerMenu &&
      !hamburgerMenu.contains(event.target)
    ) {
      isNavOpen.value = false
      document.body.style.overflow = ''
    }
  }

  onMounted(() => {
    isBrowser.value = true
    isSSR.value = false

    if (typeof window !== 'undefined') {
      isScrolled.value = window.scrollY > 50

      // Initialize countdown
      updateCountdown()
      // Update countdown every second
      countdownInterval.value = setInterval(updateCountdown, 1000)

      // Add event listeners
      window.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleResize)
      document.addEventListener('click', handleClickOutside)

      handleResize()
    }

    nextTick(() => {
      initialRender.value = true
    })
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('click', handleClickOutside)
      document.body.style.overflow = ''
    }

    // Clear countdown interval
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value)
    }
  })
</script>

<style scoped>
  /* Gradient background for the promo alert */
  .gradient-background {
    background: linear-gradient(to right, #514a9d, #ff4242, #0db4f3);
    background-size: 300% 100%;
    border: none;
  }

  /* Simple countdown timer */
  .countdown-timer {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    color: white;
    background: rgba(255, 255, 255, 0.15);
    padding: 0.15rem 0.35rem;
    margin-left: 0.5rem;
    border-radius: 3px;
    letter-spacing: 0.02em;
    border: 1px solid rgba(255, 255, 255, 0.2);
    white-space: nowrap;
  }

  .promo-text {
    text-align: center;
  }

  @media (max-width: 768px) {
    .countdown-timer {
      font-size: 0.75rem;
      padding: 0.1rem 0.25rem;
    }

    .promo-text {
      font-size: 0.9rem;
    }
  }

  .main-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    padding: 0;
    transition: background-color 0.3s ease;
    background-color: #092e58;
  }

  .main-header.scrolled {
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .nav-bar {
    position: relative;
    padding: 1.15rem 0;
  }

  .desktop-nav {
    display: flex;
    align-items: center;
  }

  .logo img {
    margin-top: 8px;
    height: 30px;
    width: 221px;
    transition: opacity 0.3s ease;
    transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  .nav-badge {
    position: relative;
    top: 1px;
  }

  .nav-link {
    text-decoration: none;
    padding: 0.5rem 0.5rem;
    border-radius: 4px;
    transform: translateZ(0);
    will-change: color, background-color;
  }

  .offer-link {
    text-decoration: underline;
    text-decoration-color: white;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
    transition:
      opacity 0.3s ease,
      text-decoration-color 0.3s ease;
  }

  .offer-link:hover {
    opacity: 0.8;
    text-decoration-color: rgba(255, 255, 255, 0.6);
  }

  .offer-link:hover::after {
    opacity: 0.6;
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
  }

  .nav-link:hover {
    color: #0db4f3;
  }

  .nav-link.-light:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .nav-link.-dark:hover {
    color: var(--color-primary-600);
    background-color: var(--color-primary-100);
  }

  .main-header:not(.scrolled) .nav-link {
    color: white;
  }

  .main-header:not(.scrolled) .nav-link:hover {
    color: white;
  }

  .main-header.scrolled .nav-link {
    color: #5d594f;
  }

  .main-header.scrolled .nav-link:hover {
    color: var(--color-primary);
  }

  .hamburger-menu {
    position: relative;
    top: -5px;
    left: -8px;
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 1.16rem 0;
    z-index: 21;
    transition: all 0.3s ease;
  }

  .hamburger-menu span {
    display: block;
    width: 25px;
    height: 3px;
    margin: 5px auto;
    background-color: #5d594f;
    transition: all 0.3s ease-in-out;
    transform: translateZ(0);
  }

  .main-header:not(.scrolled) .hamburger-menu span {
    background-color: white;
  }

  .hamburger-menu.is-active span:nth-child(2) {
    opacity: 0;
  }

  .hamburger-menu.is-active span:nth-child(1) {
    transform: translateY(8px) rotate(45deg);
  }

  .hamburger-menu.is-active span:nth-child(3) {
    transform: translateY(-8px) rotate(-45deg);
  }

  .mobile-nav {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    padding: 1.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 20;
  }

  .mobile-nav-links {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 1.5rem;
  }

  .mobile-nav-link {
    font-size: 1.1rem;
    color: #092e58;
    text-decoration: none;
    padding: 1rem 0.5rem;
    border-bottom: 1px solid rgba(9, 46, 88, 0.1);
    transition: all 0.3s ease;
    font-weight: 500;
  }

  .mobile-nav-link:hover,
  .mobile-nav-link:focus {
    background-color: rgba(13, 180, 243, 0.1);
    color: #0db4f3;
    padding-left: 1rem;
  }

  .mobile-nav-link:last-child {
    border-bottom: 2px solid rgba(9, 46, 88, 0.1);
  }

  .mobile-nav-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-top: 0.5rem;
  }

  .mobile-nav-actions .button {
    width: 100%;
    text-align: center;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-weight: 600;
    transition: all 0.2s ease;
  }

  .mobile-nav-actions .button.-primary {
    background-color: #0db4f3;
    color: white;
    border: none;
  }

  .mobile-nav-actions .button.-primary:hover {
    background-color: #0a9ad2;
    transform: translateY(-2px);
  }

  .go-to-top-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background-color: var(--color-primary);
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    transform: translateZ(0);
    will-change: opacity;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .go-to-top-btn:hover {
    opacity: 0.9;
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .go-to-top-btn:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.5);
  }

  @media (max-width: 1024px) {
    .desktop-nav,
    ._display\:flex.desktop-nav {
      display: none !important;
    }

    .hamburger-menu {
      display: block;
    }

    .logo {
      position: relative;
      top: 0.3rem;
      margin-right: 50px;
    }
  }

  @media (max-width: 768px) {
    .go-to-top-btn {
      width: 40px;
      height: 40px;
    }

    .nav-bar {
      padding: 0.75rem 0;
    }
  }

  .alert.-warning {
    ----color: white;
  }
</style>
